import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useI18n } from "@/i18n/I18nContext";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import SEO from "@/components/seo/SEO";

export default function ResetPassword() {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast({ title: t("auth.resetPasswordSuccess") });
      navigate("/login");
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <SEO title="Réinitialiser le mot de passe | Kalypso Music" description="Choisissez un nouveau mot de passe pour votre compte Kalypso Music." path="/reset-password" noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <img src="/logo.png" alt="Kalypso Music" className="h-10 w-10 rounded-xl object-cover" />
          <span className="font-heading text-xl font-semibold">Kalypso Music</span>
        </Link>

        <div className="rounded-2xl border border-border bg-card p-8">
          <h1 className="font-heading text-2xl font-medium">{t("auth.resetPasswordTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("auth.resetPasswordSubtitle")}</p>

          {error && (
            <Alert variant="destructive" className="mt-4 rounded-xl">
              <AlertDescription className="text-xs">{t("auth.resetPasswordInvalid")}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="password">{t("auth.newPasswordLabel")}</Label>
              <Input id="password" type="password" required minLength={6} data-testid={AUTH.resetPasswordInput} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid={AUTH.resetPasswordSubmit} disabled={loading || !token}>
              {loading ? t("common.loading") : t("auth.resetPasswordSubmit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
